// ============================================================================
// Safe Care - Liste des produits d'assistance disponibles (côté client)
// Module : features/assistance_sociale/pages
// Route : /souscription/creation (ex onglet "Produit Safe care active")
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm / SearchInput
// Pattern : identique à ProduitAdmin.tsx (formulaire dans Tab + tableau en dessous)
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Input, Modal } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import {
  useGetAllProduits,
  useGetAllDemandesSouscription,
  useRemettreDemandeSouscriptionEnValidation,
} from '../hooks';
import { ProduitAssistance, DemandeSouscription } from '../types';
import InputForm from '../../../Components/InputT';
import SearchInput from '../../../Components/SearchInput';
import Params from '../../../Types/ParamsPicklist';

// Paramètres de recherche pour les picklists
let searchProduit: Params = {
  CodeAgence: '',
  Table: 'PRODUITASSISTANCE',
  Colonne1: 'Code',
  Colonne2: 'Nom',
  Colagence: '101',
  Choix: 22,
};

const ProduitAssistanceList = () => {
  const navigate = useNavigate();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: produits, isLoading } = useGetAllProduits();
  const { data: rejetees, isLoading: loadingRejetees } = useGetAllDemandesSouscription('REJETEE');
  const remettre = useRemettreDemandeSouscriptionEnValidation();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState<ProduitAssistance[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedProduit, setSelectedProduit] = useState<ProduitAssistance | null>(null);
  const [activeTab, setActiveTab] = useState('1');
  const [selectedRejetee, setSelectedRejetee] = useState<DemandeSouscription | null>(null);
  const [rejeteeKeys, setRejeteeKeys] = useState<React.Key[]>([]);
  const [rejeteeModal, setRejeteeModal] = useState(false);

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  useEffect(() => {
    if (produits) SetData(produits.filter((p) => (p.Statut ?? '').toUpperCase() === 'PUBLIE'));
  }, [produits]);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d: any) => d.IdProduit === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      setSelectedProduit(record);
    }
    setModifActve(false);
    setSupActive(false);
    setAnnulActive(false);
    setConsulActive(true);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onSelectRejetee = (newSelectedRowKeys: any, selectedRows: any) => {
    setRejeteeKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? (rejetees ?? []).find((d: any) => d.IdDemande === newSelectedRowKeys?.[0]);
    setSelectedRejetee(record ?? null);
  };

  const rowSelectionRejetees = {
    selectedRowKeys: rejeteeKeys,
    type: 'radio' as const,
    onChange: onSelectRejetee,
  };

  const onRevoir = (record: DemandeSouscription) => {
    setSelectedRejetee(record);
    setRejeteeKeys([record.IdDemande!]);
    setRejeteeModal(true);
  };

  const handleRemettreEnValidation = () => {
    if (!selectedRejetee) {
      SweetAlert.fire({ title: 'Sélectionnez une demande rejetée', icon: 'warning' });
      return;
    }
    const utiSai = String(user?.coduti ?? '').trim();
    if (!utiSai || utiSai.toLowerCase() === 'undefined' || utiSai.toLowerCase() === 'null') {
      SweetAlert.fire({ title: 'Session invalide', text: 'Impossible de récupérer le code utilisateur.', icon: 'error' });
      return;
    }
    setLoading(true);
    remettre.mutate(
      { idDemande: selectedRejetee.IdDemande!, utiSai },
      {
        onSuccess: (res) => {
          setLoading(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setRejeteeKeys([]);
            setSelectedRejetee(null);
            setActiveTab('1');
            navigate('/assurance/creation', { replace: true });
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      }
    );
  };

  function consult() {
    if (selectedProduit) {
      form.setFieldsValue(selectedProduit);
    }
    setAnnulActive(false);
    setModifActve(false);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    SetData((produits ?? []).filter((p) => (p.Statut ?? '').toUpperCase() === 'PUBLIE'));
    setSelectedRowKeys([]);
    setSelectedProduit(null);
  }

  function actualiser() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (produits) SetData(produits.filter((p) => (p.Statut ?? '').toUpperCase() === 'PUBLIE'));
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    }, 500);
  }

  function souscrire() {
    if (selectedProduit) {
      if (selectedProduit.Statut !== 'PUBLIE') {
        SweetAlert.fire({ title: 'Attention', text: 'Ce produit n\'est pas publié', icon: 'warning' });
        return;
      }
      navigate(`/assurance/souscription/${selectedProduit.IdProduit}`);
    }
  }

  const columns: ColumnsType<ProduitAssistance> = [
    { title: 'Code', dataIndex: 'Code', width: 100 },
    { title: 'Nom', dataIndex: 'Nom', width: 200 },
    { title: 'Catégorie', dataIndex: 'Categorie', width: 120 },
    { title: 'Description', dataIndex: 'Description', ellipsis: true },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          disabled={record.Statut !== 'PUBLIE'}
          onClick={() => navigate(`/assurance/souscription/${record.IdProduit}`)}
        >
          Souscrire
        </Button>
      ),
    },
  ];

  const columnsRejetees: ColumnsType<DemandeSouscription> = [
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Nom', dataIndex: 'NOMCLI', width: 140 },
    { title: 'Prénom', dataIndex: 'PRECLI', width: 140 },
    { title: 'Produit', dataIndex: 'NomProduit', width: 160 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100, align: 'right' },
    { title: 'Date effet', dataIndex: 'DateEffetSouhaitee', width: 120 },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => onRevoir(record)}>
          Revoir
        </Button>
      ),
    },
  ];

  // ==================== ONGLET FORMULAIRE PRODUIT (Tab1) ====================
  // Ce tab contient le formulaire avec picklists, comme Tab1 dans ProduitAdmin.tsx
  // Côté client : champs en lecture seule (type="label")
  const FormTab = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 1 }}>
            <legend>Produit Safe Care</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdProduit" hidden>
                  <input type="hidden" />
                </Form.Item>
                <SearchInput
                  width={9}
                  form={form}
                  Titre="Recherche Produit"
                  label="Code Produit"
                  name="Code"
                  Parametre={searchProduit}
                />
                <InputForm width={9} label="Nom" name="Nom" type="label" />
                <InputForm width={9} label="Catégorie" name="Categorie" type="label" />
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Description" name="Description" type="label" />
                <InputForm width={9} label="Statut" name="Statut" type="label" />
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  // ==================== ONGLET DEMANDES REJETEES (Tab2) ====================
  const RejeteesContent = () => {
    return (
      <Box
        fontStyle="inherit"
        className="border border-left"
        width="85%"
        position="absolute"
        sx={{ p: 1 }}
      >
        <legend>Demandes rejetées</legend>
        <Spin spinning={loadingRejetees}>
          <Table
            size="small"
            style={{ width: '100%' }}
            rowKey="IdDemande"
            rowSelection={rowSelectionRejetees}
            columns={columnsRejetees}
            dataSource={rejetees ?? []}
            scroll={{ y: 250, x: 10 }}
            pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
          />
        </Spin>
      </Box>
    );
  };

  let items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Produit Safe care active',
      children: FormTab(),
    },
  ];

  if (rejetees && rejetees.length > 0) {
    items.push({
      key: '2',
      label: 'Demandes rejetées',
      children: <RejeteesContent />,
    });
  }

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>{window.location.pathname}</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={activeTab === '1' && !ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={() => {}}
          onFinishFailed={() => {}}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG" style={{ top: activeTab === '2' ? '50%' : undefined }}>
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={ajoutActve}
                  onClick={souscrire}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Souscrire
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={activeTab !== '2' || !selectedRejetee}
                  onClick={handleRemettreEnValidation}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Renvoyer en validation
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau en dessous du formulaire - visible seulement sur l'onglet Produit (hors Form pour permettre le clic radio) */}
        {activeTab === '1' && (
          <Box display="flex" fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
            <Spin spinning={isLoading || loading}>
              <div>
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdProduit"
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columns}
                  dataSource={Data}
                  scroll={{ y: 250, x: 10 }}
                  pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
                />
              </div>
            </Spin>
          </Box>
        )}
      </Spin>

      <Modal
        title={`Demande rejetée #${selectedRejetee?.IdDemande ?? ''}`}
        open={rejeteeModal}
        onOk={() => setRejeteeModal(false)}
        onCancel={() => setRejeteeModal(false)}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
        className="safeCareModal"
      >
        {selectedRejetee && (
          <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
            <Box fontStyle="inherit" sx={{ width: '50%' }}>
              <p><b>Client :</b> {selectedRejetee.Numcli}</p>
              <p><b>Nom :</b> {selectedRejetee.NOMCLI}</p>
              <p><b>Prénom :</b> {selectedRejetee.PRECLI}</p>
              <p><b>Produit :</b> {selectedRejetee.NomProduit}</p>
            </Box>
            <Box fontStyle="inherit" sx={{ width: '50%' }}>
              <p><b>Version :</b> {selectedRejetee.NumeroVersion}</p>
              <p><b>Cotisation :</b> {selectedRejetee.MontantCotisation}</p>
              <p><b>Date effet :</b> {selectedRejetee.DateEffetSouhaitee}</p>
              <p><b>Motif rejet :</b> {selectedRejetee.MotifRejet}</p>
            </Box>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default ProduitAssistanceList;